export const slotSeparator = " – ";

// Matches backend/models/schemas.py MAX_BOOKING_WINDOW_DAYS/MIN_BOOKING_LEAD_DAYS — keep these in sync.
const MAX_BOOKING_WINDOW_DAYS = 30;
const MIN_BOOKING_LEAD_DAYS = 2;

function istTodayIso() {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
  const lookup = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${lookup.year}-${lookup.month}-${lookup.day}`;
}

function addDaysIso(iso, days) {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function getEarliestBookingDateIso() {
  return addDaysIso(istTodayIso(), MIN_BOOKING_LEAD_DAYS);
}

export function getLatestBookingDateIso() {
  return addDaysIso(istTodayIso(), MAX_BOOKING_WINDOW_DAYS);
}

// Every date inside the booking window, oldest first. Used where the choice
// has to be a list rather than a free date field — a date input can stop you
// picking outside a range, but not picking a day nobody works.
export function getBookingDateOptions() {
  const today = istTodayIso();
  return Array.from({ length: MAX_BOOKING_WINDOW_DAYS - MIN_BOOKING_LEAD_DAYS + 1 }, (_, index) =>
    addDaysIso(today, MIN_BOOKING_LEAD_DAYS + index),
  );
}

// Home collection is our own round, arranged with the pathology partner
// offline, so it runs on our working days rather than anything a provider
// publishes. Matches HOME_COLLECTION_WEEKDAYS in backend/models/schemas.py —
// keep these in sync.
const HOME_COLLECTION_WEEKDAYS = [1, 2, 3, 4, 5];

export function isHomeCollectionDay(iso) {
  return HOME_COLLECTION_WEEKDAYS.includes(new Date(`${iso}T00:00:00Z`).getUTCDay());
}

export function getHomeCollectionDateOptions() {
  return getBookingDateOptions().filter(isHomeCollectionDay);
}

export function getEarliestHomeCollectionDateIso() {
  return getHomeCollectionDateOptions()[0] || getEarliestBookingDateIso();
}

// A lab order is a phlebotomist at a door and nothing else — no centre visit
// to tie it to our working days — so the partner runs that round at the
// weekend too. Matches LAB_COLLECTION_WEEKDAYS in backend/models/schemas.py.
export function getLabCollectionDateOptions() {
  return getBookingDateOptions();
}

export const appointmentSlots = Array.from({ length: 24 }, (_, index) => {
  const startMinutes = 7 * 60 + index * 30;
  const endMinutes = startMinutes + 30;
  const format = (minutes) => `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
  return `${format(startMinutes)}${slotSeparator}${format(endMinutes)}`;
});

// Matches backend/models/schemas.py available_consultation_slots() — 1-hour
// consultation slots between 10:00 and 17:00, keep in sync.
export const consultationSlots = Array.from({ length: 7 }, (_, index) => {
  const startMinutes = 10 * 60 + index * 60;
  const endMinutes = startMinutes + 60;
  const format = (minutes) => `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
  return `${format(startMinutes)}${slotSeparator}${format(endMinutes)}`;
});

// "2026-09-18" -> "18 Sep 2026". Parsed as UTC so the stored ISO date is never
// shifted a day by the viewer's timezone.
export function formatBookingDate(iso) {
  if (!iso) return "";
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}
